import { useEffect, useState } from 'react';
import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Input from '../Input';
import Chip2 from '../Chip2';
import { Box, CircularProgress } from '@mui/material';
import styled from 'styled-components';

type Props = {
	multiple?: boolean;
	validator?: (value: any) => boolean;
	label?: string;
	value: any;
	options: any[];
	valueKey: string;
	labelKey: string;
	iconKey?: string;
	error?: boolean;
	helperText?: string | undefined;
	errorText?: string | undefined;
	onChange: React.Dispatch<React.SetStateAction<any>>;
	onBlur?: (arg0: string | null) => void;
	isLoading?: boolean;
	placeholder?: string;
	showIcon?: boolean;
	classNameOndropLI?: string;
	disabled?: boolean;
	EndIcon?: React.ReactNode;
	readOnly?;
};
const AUTOCOMPLETE = styled.div`
	position: relative;
	& > div > div > div.MuiFormControl-root.ff-input {
		padding: 0;
	}

	&.ff-autocomplete {
		& .showIcon {
			& > div:first-child {
				padding: 8px 12px 8px 11.8px;
			}
			& .MuiAutocomplete-popper {
				.MuiAutocomplete-paper {
					ul {
						li {
							display: flex;
							gap: 12px;
							align-items: center;
							padding: 10px 12px;
							cursor: pointer;
							display: none;
						}
					}
				}
			}
		}
		.MuiAutocomplete-root {
			.ff-ecom-search-input .MuiAutocomplete-inputRoot {
				padding: 6px 12px;
				> input {
					font-size: 14px;
				}
			}
			.MuiAutocomplete-inputRoot {
				padding: 12px;

				.MuiAutocomplete-input {
					padding: 0;
				}
			}

			&.MuiAutocomplete-hasClearIcon {
				.MuiFormLabel-root {
					transform: translate(14px, -9px) scale(1);
				}
			}

			&.multiple {
				.MuiTextField-root {
					padding: 5px;
				}

				.MuiButtonBase-root {
					&[title='Clear'] {
						display: none;
					}
				}

				.MuiAutocomplete-inputRoot {
					padding: 7px;
					gap: 3px;
				}

				.MuiInputBase-root {
					position: initial;
					max-height: 92px;
					overflow: auto;
				}
			}
		}

		.helper-text {
			padding: 0 12px;
			color: var(--black-60);

			&.error {
				color: var(--vibrant-pink-400);
			}
		}
	}

	.MuiAutocomplete-popper {
		z-index: 9999;
		.MuiAutocomplete-paper {
			ul {
				li {
					display: flex;
					gap: 12px;
					align-items: center;
					padding: 4px 12px;
					cursor: pointer;
				}
			}
		}
		.MuiAutocomplete-noOptions {
			padding: 12px;
			&:hover {
				background-color: rgba(0, 0, 0, 0.04) !important;
			}
		}
		.MuiAutocomplete-option[aria-selected='true'] {
			background-color: #e6f1fd !important;
		}
	}
`;
const AutoComplete = ({
	multiple = false,
	validator,
	label,
	value,
	options,
	valueKey = 'value',
	labelKey = 'name',
	iconKey,
	onChange,
	onBlur,
	error = false,
	helperText = undefined,
	errorText = undefined,
	isLoading = false,
	placeholder,
	showIcon = true,
	classNameOndropLI = '',
	disabled,
	readOnly,
}: Props) => {
	const [focused, setFocused] = useState(-1);
	const [text, setText] = useState(helperText);
	const [isError, setIsError] = useState(error);

	useEffect(() => {
		return () => setFocused(-1);
	}, []);

	useEffect(() => {
		if ((error || isError) && errorText && errorText?.length > 0) {
			setText(errorText);
		} else {
			setText(helperText);
		}
	}, [error, isError]);

	useEffect(() => {
		if (multiple && validator) {
			const err = value.filter((val: any) => validator(val[valueKey]));
			if (err.length > 0) {
				setIsError(true);
			} else {
				setIsError(false);
			}
		}
	}, [value]);

	return (
		<AUTOCOMPLETE className='ff-autocomplete'>
			<Autocomplete
				// open
				disablePortal
				disabled={disabled}
				clearIcon={false}
				className={multiple ? 'multiple' : 'swapnil'}
				multiple={multiple}
				filterSelectedOptions={multiple}
				forcePopupIcon={!multiple}
				freeSolo={multiple}
				options={options}
				getOptionLabel={(option) => option[labelKey]}
				isOptionEqualToValue={(option, value) =>
					option[valueKey] === value[valueKey]
				}
				value={value}
				onChange={(event: any, newValue: any | null) => {
					setFocused(-1);
					if (multiple) {
						const last = newValue[newValue.length - 1];
						if (typeof last === 'string') {
							const newOption: any = {};
							newOption[valueKey] = last;
							newOption[labelKey] = last;
							onChange([...value, newOption]);
						} else {
							onChange(newValue);
							onBlur && onBlur(newValue);
						}
					} else {
						onChange(newValue ? newValue : null);
						onBlur && onBlur(newValue ? newValue[valueKey] : null);
					}
				}}
				blurOnSelect={!multiple}
				renderInput={(params) => (
					<Input
						className={`${
							!showIcon
								? 'showIcon'
								: classNameOndropLI === 'ff-ecom-search-dropdown'
								? 'ff-ecom-search-input'
								: ''
						}`}
						{...params}
						label={value ? label : ''}
						placeholder={placeholder ?? label}
						error={error || isError}
						onBlur={() => setFocused(-1)}
						readOnly={readOnly}
						onKeyDown={(event) => {
							const key = event.key;
							if (multiple) {
								if (key === 'ArrowLeft') {
									setFocused((prev) =>
										prev === -1 ? value.length - 1 : prev - 1
									);
								} else if (key === 'ArrowRight') {
									setFocused((prev) => (prev === value.length ? 0 : prev + 1));
								} else if (key === 'Delete' || key === 'Backspace') {
									if (focused > -1 && focused < value.length) {
										event.preventDefault();
										event.stopPropagation();
										onChange(
											value.filter((v: any, index: number) => index !== focused)
										);
										setFocused(-1);
									}
								}
							}
						}}
						InputProps={{
							...params.InputProps,
							// startAdornment: (
							// 	<>
							// 		{iconKey && value && (
							// 			<Avatar
							// 				sx={{ width: 24, height: 24 }}
							// 				alt=''
							// 				src={value[iconKey] ?? ''}
							// 			/>
							// 		)}
							// 	</>
							// ),
							endAdornment: (
								<>
									{showIcon ? (
										<>
											<div style={{ color: '#3b4fd0' }}>
												{isLoading && (
													<CircularProgress color='inherit' size={16} />
												)}
											</div>
											{params.InputProps.endAdornment}
										</>
									) : null}
								</>
							),
						}}
					/>
				)}
				renderOption={(props, option) => {
					const isSelected = Array.isArray(value)
						? value.some((val) => val[valueKey] === option[valueKey]) // For multiple selections
						: value && value[valueKey] === option[valueKey]; // For single selection
					return (
						<Box
							component='li'
							{...props}
							className={`${classNameOndropLI} MuiAutocomplete-option`}
						>
							{/* {iconKey && option[iconKey]} */}
							<p className='ff-chatbot-body1'>{option[valueKey]}</p>
							{isSelected && iconKey && option[iconKey]}
						</Box>
					);
				}}
				renderTags={(values, getTagProps) =>
					values.map((option, index) => {
						const err = (validator && validator(option[valueKey])) ?? false;
						let clasName = focused === index ? 'focused' : '';
						clasName += err ? ' error' : '';
						setIsError(err);

						return (
							<Chip2
								label={option[labelKey]}
								{...getTagProps({ index })}
								onClick={() => setFocused(index)}
								className={clasName}
							/>
						);
					})
				}
			/>
			{text?.length && (
				<p
					style={{ marginTop: '2px' }}
					className={`ff-chatbot-caption helper-text${
						error || isError ? ' error' : ''
					}`}
				>
					{text}
				</p>
			)}
		</AUTOCOMPLETE>
	);
};
export default AutoComplete;
